import { addSlideFooter } from '../slide_footer';
import { LABEL_COLOR, TITLE_TEXT } from '../constants';

// Height stops above the compact slide footer.
const AREA = { h: 4.3, w: 9.6, x: 0.2, y: 0.9 };
const H_GAP = 0.18;
const V_GAP = 0.14;
const ACCENT_W = 0.06;
const PAD = 0.12;
const TITLE_COLOR = '20343A';
const DESC_COLOR = '8A8A8A';
const BORDER_COLOR = 'D9D9D9';
const DESCRIPTION_LINE_H = 0.13;
const METRIC_ROW_H = 0.15;
const MIN_CARD_H = 0.72;

const estimateDescriptionLines = (description = '') =>
  description
    ? description
        .split('\n')
        .reduce((total, line) => total + Math.max(1, Math.ceil(line.length / 62)), 0)
    : 0;

const getDesiredCardHeight = (card) =>
  Math.max(
    MIN_CARD_H,
    0.38 +
      estimateDescriptionLines(card.description) * DESCRIPTION_LINE_H +
      card.rows.length * METRIC_ROW_H
  );

const groupCardRows = (cards) => {
  const grouped = cards.reduce((rows, card) => {
    (rows[card.row] = rows[card.row] || []).push(card);
    return rows;
  }, {});

  return Object.keys(grouped)
    .map(Number)
    .sort((a, b) => a - b)
    .map((row) => ({
      cards: grouped[row],
      height: Math.max(...grouped[row].map(getDesiredCardHeight)),
    }));
};

const paginateRows = (rows) =>
  rows.reduce(
    (pages, row) => {
      const page = pages[pages.length - 1];
      const usedHeight = page.reduce((total, item) => total + item.height, 0);
      const gapsHeight = V_GAP * page.length;
      if (page.length && usedHeight + gapsHeight + row.height > AREA.h) pages.push([row]);
      else page.push(row);
      return pages;
    },
    [[]]
  );

const addCard = ({ card, cardH, cellW, pptx, slide, y }) => {
  const cardW = cellW - H_GAP;
  const x = AREA.x + card.col * cellW + H_GAP / 2;
  const innerX = x + PAD + ACCENT_W;
  const innerW = cardW - PAD * 2 - ACCENT_W;

  slide.addShape(pptx.shapes.ROUNDED_RECTANGLE, {
    fill: { color: 'FFFFFF' },
    h: cardH,
    line: { color: BORDER_COLOR, width: 1 },
    rectRadius: 0.03,
    w: cardW,
    x,
    y,
  });
  slide.addShape(pptx.shapes.RECTANGLE, {
    fill: { color: card.color },
    h: cardH - 0.04,
    line: { type: 'none' },
    w: ACCENT_W,
    x: x + 0.02,
    y: y + 0.02,
  });

  slide.addText(card.title, {
    align: 'left',
    bold: true,
    color: TITLE_COLOR,
    fit: 'shrink',
    fontFace: 'Arial',
    fontSize: 7,
    h: 0.16,
    valign: 'top',
    w: innerW,
    x: innerX,
    y: y + 0.05,
  });

  const descriptionHeight = estimateDescriptionLines(card.description) * DESCRIPTION_LINE_H;
  let contentTop = y + 0.25;
  if (card.description) {
    slide.addText(card.description, {
      align: 'left',
      color: DESC_COLOR,
      fit: 'shrink',
      fontFace: 'Arial',
      fontSize: 5.5,
      h: descriptionHeight,
      margin: 0,
      valign: 'top',
      w: innerW,
      x: innerX,
      y: y + 0.22,
    });
    contentTop = y + 0.24 + descriptionHeight;
  }

  const rowsHeight = Math.max(0, y + cardH - contentTop - 0.06);
  const rowHeight = card.rows.length ? rowsHeight / card.rows.length : 0;
  card.rows.forEach((row, index) => {
    const rowY = contentTop + index * rowHeight;
    if (index > 0) {
      slide.addShape(pptx.shapes.LINE, {
        h: 0.001,
        line: { color: 'E6E6E6', dashType: 'dash', width: 0.5 },
        w: innerW,
        x: innerX,
        y: rowY,
      });
    }
    slide.addText(row.label, {
      align: 'left',
      color: LABEL_COLOR,
      fit: 'shrink',
      fontFace: 'Arial',
      fontSize: 5.5,
      h: rowHeight,
      margin: 0,
      valign: 'middle',
      w: innerW * 0.62,
      x: innerX,
      y: rowY,
    });
    slide.addText(row.value, {
      align: 'right',
      bold: true,
      color: TITLE_COLOR,
      fit: 'shrink',
      fontFace: 'Arial',
      fontSize: 5.5,
      h: rowHeight,
      margin: 0,
      valign: 'middle',
      w: innerW * 0.38,
      x: innerX + innerW * 0.62,
      y: rowY,
    });
  });
};

/**
 * Creates one or more native card-grid slides. Logical grid rows stay together; dense grids continue
 * onto additional slides instead of shrinking their editable text until it overlaps.
 */
export const createCardGridSlide = ({
  cards = [],
  columns = 3,
  fitOnSingleSlide = false,
  pptx,
  reportDateRange,
  reportType,
  reportUrl,
  shopperAttributes,
  slideTitle,
  title,
}) => {
  const rows = groupCardRows(cards);
  const pages = fitOnSingleSlide ? [rows] : cards.length ? paginateRows(rows) : [[]];
  const cellW = AREA.w / columns;

  const slides = pages.map((page, pageIndex) => {
    const slide = pptx.addSlide();
    const baseTitle = slideTitle || title || '';
    const pageTitle = pageIndex
      ? `${baseTitle} (continued ${pageIndex + 1}/${pages.length})`
      : baseTitle;
    slide.addText(pageTitle, { ...TITLE_TEXT, fontSize: 14 });

    const rowHeight = page.length ? (AREA.h - V_GAP * (page.length - 1)) / page.length : 0;
    let y = AREA.y;
    page.forEach((row) => {
      const cardH = fitOnSingleSlide ? rowHeight : Math.min(row.height, AREA.h);
      row.cards.forEach((card) => addCard({ card, cardH, cellW, pptx, slide, y }));
      y += cardH + V_GAP;
    });

    addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });
    return slide;
  });

  return slides[0];
};
